import { makeResetStyles, makeStyles, mergeClasses } from '@fluentui/react-components';
import { tokens } from '@fluentui-copilot/tokens';
import type { ReferenceListSlots, ReferenceListState } from './ReferenceList.types';
import type { SlotClassNames } from '@fluentui/react-components';

export const referenceListClassNames: SlotClassNames<ReferenceListSlots> = {
  root: 'fai-ReferenceList',
  arrowableRegion: 'fai-ReferenceList__arrowableRegion',
  showMoreButton: 'fai-ReferenceList__showMoreButton',
  showLessButton: 'fai-ReferenceList__showLessButton',
};

/**
 * Styles for the root slot
 */
const useRootBaseClassName = makeResetStyles({
  display: 'flex',
  flexDirection: 'row',
  gap: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
});

const useRootStyles = makeStyles({
  referencesExpanded: {
    flexWrap: 'wrap',
  },
});

const useArrowableRegionBaseClassName = makeResetStyles({
  display: 'contents',
  maxWidth: '100%',
});

const useOverflowButtonBaseClassName = makeResetStyles({
  display: 'inline-flex',
  flexShrink: 0,
});

/**
 * Apply styling to the ReferenceList slots based on the state
 */
export const useReferenceListStyles_unstable = (state: ReferenceListState): ReferenceListState => {
  'use no memo';

  const { areReferencesExpanded, shouldUseOverflow } = state;

  const rootBaseClassName = useRootBaseClassName();
  const arrowableRegionBaseClassName = useArrowableRegionBaseClassName();
  const overflowButtonBaseClassName = useOverflowButtonBaseClassName();
  const rootStyles = useRootStyles();

  state.root.className = mergeClasses(
    referenceListClassNames.root,
    rootBaseClassName,
    (areReferencesExpanded || !shouldUseOverflow) && rootStyles.referencesExpanded,
    state.root.className,
  );

  state.arrowableRegion.className = mergeClasses(
    referenceListClassNames.arrowableRegion,
    arrowableRegionBaseClassName,
    state.arrowableRegion.className,
  );

  if (state.showMoreButton) {
    state.showMoreButton.className = mergeClasses(
      referenceListClassNames.showMoreButton,
      overflowButtonBaseClassName,
      state.showMoreButton.className,
    );
  }

  if (state.showLessButton) {
    state.showLessButton.className = mergeClasses(
      referenceListClassNames.showLessButton,
      overflowButtonBaseClassName,
      state.showLessButton.className,
    );
  }

  return state;
};
